import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import {
  ENTITLEMENT_GRAPH_TYPE_OPTION,
  ESTIMATED_CURRENT_PRODUCTION_BBLD,
  ESTIMATED_CURRENT_PRODUCTION_BOED,
  ESTIMATED_CURRENT_PRODUCTION_MMCFD,
  enforceMutualExclusivity,
  REMAINING_GAS_RESERVES,
  REMAINING_OIL_AND_CONDENSATE_RESERVES,
  REMAINING_RESERVES,
} from '@/pages/PortfolioAnalysis/PortfolioGraphing/utils';

interface GraphTypeOption {
  name: string;
  [key: string]: unknown;
}

interface GraphType {
  value?: string;
  [key: string]: unknown;
}

interface EntitlementProps {
  graphTypeOptions?: GraphTypeOption[];
  graphType?: GraphType;
  handleSetState: (value: unknown, key: string) => void;
}

// Metrics that support entitlement filtering
// Values from app/javascript/pages/PortfolioAnalysis/PortfolioGraphing/utils.js
const ENTITLEMENT_SUPPORTED_METRICS: readonly string[] = [
  ESTIMATED_CURRENT_PRODUCTION_BOED.value, // Only boed supports entitlement for production
  REMAINING_RESERVES.value,
  REMAINING_OIL_AND_CONDENSATE_RESERVES.value,
  REMAINING_GAS_RESERVES.options[0]?.value, // Gas Reserves Bcf
  REMAINING_GAS_RESERVES.options[1]?.value, // Gas Reserves Bcm
].filter((value): value is string => value !== undefined);

const rootStyles: SxProps<Theme> = theme => ({
  width: '100%',
  borderTop: 'solid thin rgba(0, 0, 0, 0.12)',
  paddingTop: theme.spacing(2),
  marginTop: theme.spacing(1),
});

const labelStyles: SxProps<Theme> = theme => ({
  color: theme.palette.alternateText.color,
  fontWeight: 'bold',
});

const checkboxRootStyles: SxProps<Theme> = {
  width: '100%',
};

const checkboxRowLabelStyles: SxProps<Theme> = {
  width: '100%',
  justifyContent: 'space-between',
  margin: 0,
};

const disabledLabelStyles: SxProps<Theme> = {
  color: 'rgba(0, 0, 0, 0.26)',
};

const Entitlement = ({ graphTypeOptions = [], graphType, handleSetState }: EntitlementProps) => {
  const { portfolioEntitlement: entitlementEnabled } = useGetFeatureFlags();

  // If feature flag is disabled, don't render the component
  if (!entitlementEnabled) {
    return null;
  }

  // Determine if entitlement should be disabled based ONLY on selected metric
  const isDisabled = (): boolean => {
    // Enable only for specific metrics
    // Note: bbld and mmcfd are excluded because entitlement data only has boed
    const graphTypeValue = graphType?.value ?? '';
    return !ENTITLEMENT_SUPPORTED_METRICS.includes(graphTypeValue);
  };

  const getTooltipText = (): string => {
    if (!isDisabled()) {
      return 'Display entitlement production';
    }

    const graphTypeValue = graphType?.value;
    const isNonBoeProduction =
      graphTypeValue === ESTIMATED_CURRENT_PRODUCTION_BBLD.value ||
      graphTypeValue === ESTIMATED_CURRENT_PRODUCTION_MMCFD.value;

    if (isNonBoeProduction) {
      return 'Entitlement is only available for boe/d. Use boe/d to view entitlement data for production metrics.';
    }

    return 'Can only be selected for production (boe/d) and reserves metrics';
  };

  const tooltipText = getTooltipText();

  const isChecked = graphTypeOptions.some(
    option => option.name === ENTITLEMENT_GRAPH_TYPE_OPTION.name,
  );

  const handleClick = (): void => {
    let newOptions: GraphTypeOption[];
    if (isChecked) {
      // Remove Entitlement from graphTypeOptions
      newOptions = graphTypeOptions.filter(
        option => option.name !== ENTITLEMENT_GRAPH_TYPE_OPTION.name,
      );
    } else {
      // Add Entitlement to graphTypeOptions
      newOptions = [...graphTypeOptions, { name: ENTITLEMENT_GRAPH_TYPE_OPTION.name }];
    }
    // Enforce mutual exclusivity rules defined in utils
    const filteredOptions = enforceMutualExclusivity(newOptions, graphTypeOptions);
    handleSetState(filteredOptions, 'graphTypeOptions');
  };

  const disabled = isDisabled();

  return (
    <Box sx={rootStyles}>
      <Typography sx={labelStyles} variant='overline'>
        Entitlement
      </Typography>
      <FormControl component='fieldset' sx={checkboxRootStyles}>
        <Tooltip title={tooltipText}>
          <FormControlLabel
            sx={checkboxRowLabelStyles}
            labelPlacement='start'
            value={true}
            control={<Checkbox color='primary' checked={isChecked} onClick={handleClick} />}
            label={
              <Typography sx={[labelStyles, disabled && disabledLabelStyles]}>
                Entitlement
              </Typography>
            }
            disabled={disabled}
          />
        </Tooltip>
      </FormControl>
    </Box>
  );
};

export default Entitlement;
